import type { SourcePostFilters } from './types.ts';
import { obnizkaCeny } from './obnizka.ts';

/**
 * WSTĄŻKA NA ZDJĘCIU KARTY — kolejność, jedna dla strony wyników, strony oferty i e-maili z
 * alertami (plan §1.68, docs/AlertEmails.md §3.1). Tu, w czystym module zgodnym z Deno, bo
 * `send-filter-alerts` musi wybrać TĘ SAMĄ wstążkę co karta, a `src/lib/wyroznikiOferty.ts` (pełny
 * katalog wyróżników) ciągnie zależności, których Deno nie rozwiąże. `wyroznikiOferty.ts` importuje
 * stąd i eksportuje dalej.
 *
 * Napisu tu nie ma: strona bierze klucz i18n, e-mail własny napis („Flat Hero” dwoma słowami).
 */

/**
 * Kolejność wstążki (Piotr 7.10): jedna, pierwsza z tej listy, którą oferta ma i której najemca
 * nie ma w filtrach. Zmiana kolejności albo zestawu = zmiana tej stałej. Celowo poza listą:
 * „Parking w cenie” i „Przyjazne zwierzętom” (tylko pastylka / lista cech).
 */
export const KOLEJNOSC_WSTAZKI_KARTY = [
  'zweryfikowany',
  'wlasciciel',
  'tanio',
  'obnizka',
  'media_w_cenie',
  'garaz_w_cenie',
] as const;

/** Wyróżniki, które mogą stanąć na wstążce. */
export type IdWstazki = (typeof KOLEJNOSC_WSTAZKI_KARTY)[number];

/** Najmniej, czego potrzebuje wybór wstążki: id i to, czy najemca ma tę cechę w filtrach. */
export interface KandydatWstazki {
  id: string;
  pasuje: boolean;
}

/**
 * Pierwszy z `KOLEJNOSC_WSTAZKI_KARTY`, którego najemca NIE ma w filtrach — cecha z filtra jest w
 * pastylkach z ✓ (wariant 2), a na każdej karcie listy byłaby tą samą wstążką co w filtrze.
 */
export function wyroznikWstazki<W extends KandydatWstazki>(lista: readonly W[]): W | null {
  for (const id of KOLEJNOSC_WSTAZKI_KARTY) {
    const w = lista.find((x) => x.id === id && !x.pasuje);
    if (w) return w;
  }
  return null;
}

/** Wstążka KARTY. Od 7.10 ta sama co `wyroznikWstazki` (zaufanie stoi na początku kolejności). */
export function wyroznikWstazkiKarty<W extends KandydatWstazki>(lista: readonly W[]): W | null {
  return wyroznikWstazki(lista);
}

/** Pola oferty, z których bierze się kandydatów na wstążkę (kolumny `SERP_COLUMNS`). */
export interface OfertaDoWstazki {
  owner_confirmed?: boolean | null;
  owner_present?: boolean | null;
  taniej_o_procent?: number | null;
  rent_reduced_from_pln?: number | null;
  rent_reduced_at?: string | null;
  extracted_rent_pln?: number | null;
  extracted_extras?: unknown;
}

/**
 * Wstążka oferty w kontekście zestawu — to, co karta wyliczy z pełnego katalogu
 * (`wyroznikWstazkiKarty(wyroznikiOferty(o, { filtry, teraz }))`), policzone z samych kandydatów
 * wstążkowych. Definicje jak `ma()` w `wyroznikiOferty.ts`; zgodność z kartą pilnuje
 * `src/lib/wyroznikiOferty.test.ts`.
 */
export function wstazkaOferty(o: OfertaDoWstazki, filtry: SourcePostFilters = {}, teraz: number = Date.now()): IdWstazki | null {
  const e = (o.extracted_extras as Record<string, unknown> | null) ?? {};
  const amenities = Array.isArray(e.amenities) ? (e.amenities as unknown[]) : [];
  const kandydaci: Array<{ id: IdWstazki; pasuje: boolean }> = [];
  if (o.owner_confirmed === true) kandydaci.push({ id: 'zweryfikowany', pasuje: false });
  else if (o.owner_present === true) kandydaci.push({ id: 'wlasciciel', pasuje: false });
  if (o.taniej_o_procent != null && o.taniej_o_procent > 0) kandydaci.push({ id: 'tanio', pasuje: false });
  if (obnizkaCeny(o, teraz) !== null) kandydaci.push({ id: 'obnizka', pasuje: false });
  if (e.media_included === true) kandydaci.push({ id: 'media_w_cenie', pasuje: false });
  if (e.parking_included === true && e.parking_type === 'underground') {
    // Jak `stanWarunku('udogodnienie.parking')`: z parkingiem w filtrach i wzmianką o parkingu
    // albo garażu cecha „pasuje”, więc idzie do pastylek z ✓, nie na wstążkę.
    const pasuje = !!filtry.udogodnienia?.includes('parking') && (amenities.includes('parking') || amenities.includes('garage'));
    kandydaci.push({ id: 'garaz_w_cenie', pasuje });
  }
  return wyroznikWstazkiKarty(kandydaci)?.id ?? null;
}
